import { Box, CircularProgress, Typography } from '@material-ui/core'
import {
  CompanySetting,
  UpdateSortContactSettingRequestContactSettingsInnerStatusEnum
} from '@noco/http-client/lib/noco'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Colors, DynamicMuiIcon } from 'src/components/atoms'
import { LayoutTeam } from 'src/components/commons'
import { ModalCustomFormCreate } from 'src/components/modals'
import { DraggableData } from 'src/components/molecules'
import { Header, SelectAttribute } from 'src/components/organisms'
import { createDraggableSettingList } from 'src/fixtures/modules/company/utils'
import { useListCompanySettings, useSortCompanySettings } from 'src/fixtures/modules/companySetting/hooks'
import { useFeatureFlag } from 'src/fixtures/modules/user/hooks'

export const PageTeamCompanySetting = () => {
  const [windowReady, setWindowReady] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [settings, setSettings] = useState<DraggableData[]>([])
  const [targetEditSetting, setTargetEditSetting] = useState<CompanySetting>()
  const { hasPermission: isCreatable } = useFeatureFlag('companySetting', 'create')
  const { hasPermission: isUpdatable } = useFeatureFlag('companySetting', 'update')
  const { data: responseCompanySettings } = useListCompanySettings()
  const { handleSortCompanySettings, isLoading: isSortLoading } = useSortCompanySettings()

  //- NOTE: handleEdit 時に find しやすいように concat
  const rawCompanySettings = useMemo(() => {
    if (responseCompanySettings)
      return responseCompanySettings?.activeCompanySettings?.concat(responseCompanySettings?.inactiveCompanySettings!)
  }, [responseCompanySettings])

  const handleClick = useCallback(() => setModalOpen(true), [])

  const handleEdit = useCallback(
    item => {
      setTargetEditSetting(rawCompanySettings?.find(setting => setting.id === item.id))
      setModalOpen(true)
    },
    [rawCompanySettings]
  )

  const handleClose = useCallback(() => {
    setTargetEditSetting(undefined)
    setModalOpen(false)
  }, [])

  const handleCancel = useCallback(() => {
    setTargetEditSetting(undefined)
    setModalOpen(false)
  }, [])

  useEffect(() => {
    if (responseCompanySettings) {
      const settingList = createDraggableSettingList(responseCompanySettings)
      setSettings(settingList)
    }
  }, [responseCompanySettings])

  useEffect(() => {
    setWindowReady(true)
  }, [])

  return (
    <>
      <LayoutTeam>
        <Box
          height="100%"
          width={1}
          px="16px"
          pb="32px"
          bgcolor={Colors.functional.background.default}
          display="flex"
          flexDirection="column"
        >
          <Header
            textSize="large"
            iconSize="32px"
            icon="listAlt"
            title="会社の項目"
            rightSection={
              <Button
                startIcon={<DynamicMuiIcon variant="add" size="16px" />}
                title="新規項目作成"
                disabled={!isCreatable}
                onClick={handleClick}
              />
            }
            back={false}
          />
          <Box mt="16px" />
          <Box px="16px">
            <Typography variant="subtitle1">会社に表示する情報を設定できます</Typography>
          </Box>
          <Box
            height={1}
            mt="16px"
            mx="16px"
            borderRadius="6px"
            border={`1px solid ${Colors.background.silver}`}
            overflow={'hidden'}
          >
            {windowReady && !isSortLoading ? (
              <SelectAttribute
                domain="company"
                items={settings}
                labelLeft="選択可能な項目"
                labelRight="会社で表示する項目"
                hasEditPermission={isUpdatable}
                onChange={items => {
                  handleSortCompanySettings({
                    companySettings:
                      items.map((item, idx) => ({
                        id: item.id,
                        sort: idx + 1,
                        status: item.isSelected
                          ? UpdateSortContactSettingRequestContactSettingsInnerStatusEnum.Active
                          : UpdateSortContactSettingRequestContactSettingsInnerStatusEnum.Inactive
                      })) || []
                  })
                }}
                onEdit={handleEdit}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Box>
      </LayoutTeam>
      <ModalCustomFormCreate
        domain="company"
        open={modalOpen}
        defaultValue={targetEditSetting}
        onClose={handleClose}
        onCancel={handleCancel}
        onConfirm={() => setModalOpen(false)}
      />
    </>
  )
}
